'use client'

import { useTranslations } from 'next-intl'
import { SectionHeader } from './SectionHeader'

export type Review = { author?: string; text?: string; rating?: number }

type Props = {
  reviews: Review[]
  avgRating?: number | null
  reviewCount?: number | null
}

/**
 * Sección de reseñas (prueba social): cards con cita, autor y rating en
 * estrellas. Theme-aware (tokens --ms-*). Degrada — no renderiza — si no hay
 * reseñas con contenido y tampoco hay stats de agregación.
 */
export function ReviewsSection({ reviews, avgRating, reviewCount }: Props) {
  const t = useTranslations('public.microsite')
  const valid = (reviews || []).filter((r) => r && (r.text || r.author)).slice(0, 6)
  const hasStats = avgRating != null && (reviewCount ?? 0) > 0
  if (valid.length === 0 && !hasStats) return null

  return (
    <section className="px-5 sm:px-8 pt-14 sm:pt-20 pb-2 max-w-6xl mx-auto">
      <SectionHeader kicker={t('reviewsKicker')} title={t('reviewsTitle')} />

      {hasStats && (
        <div className="flex items-center gap-2 mt-4 mb-2">
          <span className="text-[22px] leading-none" style={{ color: 'var(--ms-accent)' }}>★</span>
          <span className="text-[18px] font-bold" style={{ color: 'var(--ms-text)', fontFamily: 'var(--ms-font-display)' }}>
            {(avgRating as number).toFixed(1)}
          </span>
          <span className="text-[14px]" style={{ color: 'var(--ms-text-soft)' }}>
            ({reviewCount})
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mt-8">
        {valid.map((r, i) => (
          <figure
            key={i}
            className="flex flex-col p-5 sm:p-6"
            style={{
              background: 'var(--ms-surface)',
              borderRadius: 'var(--ms-radius)',
              border: '1px solid var(--ms-border)',
              boxShadow: 'var(--ms-card-shadow)',
            }}
          >
            {typeof r.rating === 'number' && r.rating > 0 && (
              <div className="flex gap-0.5 mb-3 text-[14px]" aria-label={`${r.rating} / 5`}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n} style={{ color: n <= (r.rating as number) ? 'var(--ms-accent)' : 'var(--ms-border)' }}>
                    ★
                  </span>
                ))}
              </div>
            )}
            {r.text && (
              <blockquote className="text-[15px] leading-relaxed flex-1" style={{ color: 'var(--ms-text-soft)' }}>
                “{r.text}”
              </blockquote>
            )}
            {r.author && (
              <figcaption className="mt-4 text-[13px] font-bold" style={{ color: 'var(--ms-text)', fontFamily: 'var(--ms-font-display)' }}>
                {r.author}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
    </section>
  )
}
